import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  Component,
  computed,
  inject,
  Input,
  model,
  output,
  signal,
} from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { getDocumentIcon, notify, SiaTranslatePipe } from '@sia/shared';
import { firstValueFrom } from 'rxjs';
import { DossierConfigurationComponent } from '../../dialogs/dossier-configuration/dossier-configuration.component';
import {
  SiaFileDossier,
  SiaGridFile,
  SiaRelatedFiles,
} from '../../interfaces/grid-file';
import { SiaRelatedFilesService } from '../../services/related-files-service';

@Component({
  selector: 'related-files-grid',
  standalone: true,
  imports: [
    NgTemplateOutlet,
    NgClass,
    MatIcon,
    MatProgressSpinnerModule,
    MatIconButton,
    MatMenuModule,
    SiaTranslatePipe,
  ],
  templateUrl: './related-files-grid.component.html',
  styleUrl: './related-files-grid.component.scss',
})
export class RelatedFilesGridComponent {

  #relatedFilesService = inject(SiaRelatedFilesService);
  #breakpointObserver = inject(BreakpointObserver);
  #dialog = inject(MatDialog);
  #reletedFile = signal<SiaGridFile | undefined>(undefined);
  fileSelected = output<SiaGridFile>();

  @Input() set file(value: SiaGridFile | undefined) {
    this.#reletedFile.set(value);
    this.#loadData();
  }

  viewMode = model<'dossier' | 'standard'>('standard');
  dossierForUpdate = model<SiaFileDossier>({});
  fileRemovedFromDossier = output<SiaGridFile>();

  isMobile = signal<boolean>(false);
  relatedFiles = signal<SiaGridFile[]>([]);
  dossiers = signal<SiaFileDossier[]>([]);
  isLoading = signal<boolean>(false);
  selectedFolderId = signal<'sameFolder' | number | null>(null);
  columnsCount = computed(() => (this.isMobile() ? 3 : 5));

  // Tracciamento delle animazioni
  animatingInFiles = signal<Set<string>>(new Set());
  animatingOutFiles = signal<Set<string>>(new Set());

  hasSameFolder = computed(() => this.relatedFiles().length > 0);

  isSameFolderSelected = computed(
    () => this.selectedFolderId() === 'sameFolder',
  );

  selectedDossier = computed<SiaFileDossier | null>(() => {
    const id = this.selectedFolderId();
    if (id === null || id === 'sameFolder') return null;
    return this.dossiers().find((d) => d.id === id) ?? null;
  });

  // Indice globale della selezione (0 = sameFolder se esiste, poi dossiers)
  selectedGlobalIndex = computed<number>(() => {
    const id = this.selectedFolderId();
    if (id === null) return -1;
    if (id === 'sameFolder') return 0;
    const dossierIdx = this.dossiers().findIndex((d) => d.id === id);
    if (dossierIdx === -1) return -1;
    return this.hasSameFolder() ? dossierIdx + 1 : dossierIdx;
  });

  // Numero totale di elementi nella griglia
  totalItems = computed(() => {
    return (this.hasSameFolder() ? 1 : 0) + this.dossiers().length + 1;
  });

  rowCount = computed(() => {
    const cols = this.columnsCount();

    return Math.ceil(this.totalItems() / cols);
  });

  toggleSameFolder() {
    if (this.selectedFolderId() === 'sameFolder') {
      this.selectedFolderId.set(null);
    } else {
      this.selectedFolderId.set('sameFolder');
    }
  }

  toggleDossier(dossierId: number) {
    if (this.selectedFolderId() === dossierId) {
      this.selectedFolderId.set(null);
    } else {
      this.selectedFolderId.set(dossierId);
    }
    Math.max(0, this.columnsCount() - 1);
  }

  // Verifica se l'elemento all'indice globale è nella riga espansa
  isInExpandedRow(globalIndex: number): boolean {
    const selectedIdx = this.selectedGlobalIndex();
    if (selectedIdx === -1) return false;
    const cols = this.columnsCount();
    const selectedRow = Math.floor(selectedIdx / cols);
    const itemRow = Math.floor(globalIndex / cols);
    return selectedRow === itemRow;
  }

  // Ottiene l'indice globale di un dossier
  getDossierGlobalIndex(dossierIndex: number): number {
    return this.hasSameFolder() ? dossierIndex + 1 : dossierIndex;
  }

  // Verifica se mostrare il pannello espanso dopo questa riga
  shouldShowExpandedPanelAfterRow(rowStartIndex: number): boolean {
    const selectedIdx = this.selectedGlobalIndex();
    if (selectedIdx === -1) return false;
    const cols = this.columnsCount();
    const rowEnd = rowStartIndex + cols - 1;
    return selectedIdx >= rowStartIndex && selectedIdx <= rowEnd;
  }

  // Ottiene i file della selezione corrente
  getSelectedFiles(): SiaGridFile[] {
    const id = this.selectedFolderId();
    if (id === null) return [];
    if (id === 'sameFolder') return this.relatedFiles();
    return this.selectedDossier()?.files ?? [];
  }

  async #loadData() {
    this.isLoading.set(true);
    try {
      const res: SiaRelatedFiles =
        await this.#relatedFilesService.getRelatedInformations(
          this.#reletedFile()?.sha256,
        );
      const files = (res?.files ?? []).map((f: SiaGridFile) => {
        const iconInfo = getDocumentIcon(f.name);
        return {
          ...f,
          icon: iconInfo.icon,
          color: iconInfo.color,
        };
      });
      this.relatedFiles.set(files);

      const dossiers = (res?.dossiers ?? []).map((d: SiaFileDossier) => {
        const filesWithIcons = (d.files ?? []).map((f: SiaGridFile) => {
          const iconInfo = getDocumentIcon(f.name);
          return {
            ...f,
            icon: iconInfo.icon,
            color: iconInfo.color,
          };
        });
        return {
          ...d,
          files: filesWithIcons,
        };
      });
      this.dossiers.set(dossiers);
    } catch (error) {
      console.error('Error loading related files:', error);
      this.relatedFiles.set([]);
      this.dossiers.set([]);
    } finally {
      this.isLoading.set(false);
    }
  }

  async createDossier() {
    try {
      const dialogRef = this.#dialog.open(DossierConfigurationComponent, {
        disableClose: true,
      });
      const dossierName = await firstValueFrom(dialogRef.afterClosed());
      if (dossierName) {
        const res = await this.#relatedFilesService.createDossier(dossierName, [
          this.#reletedFile()!.sha256,
        ]);
        this.dossierForUpdate.set({
          ...res,
          files: [...(res.files ?? [])],
        });
        this.changeMode();
        notify(
          'Dossier' + dossierName + 'creato con successo',
          2000,
          'success',
        );
      }
    } catch (error) {
      notify('Errore nella creazione del dossier', 2000, 'error');
    }
  }

  updateDossier(dossierId: number) {
    const dossier = this.dossiers().find((item) => item.id === dossierId);
    if (!dossier) return;
    this.dossierForUpdate.set({
      ...dossier,
      files: [...(dossier.files ?? [])],
    });
    this.changeMode();
  }
  changeMode() {
    if (this.viewMode() === 'standard') {
      this.viewMode.set('dossier');
    } else {
      this.viewMode.set('standard');
      this.dossierForUpdate.set({});
    }
  }

  async deleteDossier(dossierId: number) {
    try {
      await this.#relatedFilesService.deleteDossier(dossierId);
      let d = this.dossiers().filter((doss) => doss.id != dossierId);
      this.dossiers.set([...d]);
      notify('dossier eliminato con successio', 2000, 'success');
    } catch (error) {
      notify(`errore durante eliminazione del dossier`, 2000, 'error');
    }
  }

  removeFilesFromDossier(file: SiaGridFile) {
    try {
      // Avvia l'animazione di uscita verso sinistra
      this.animatingOutFiles.update((set) => new Set(set).add(file.sha256));

      // Notifica subito il componente padre per avviare l'animazione di entrata nella griglia
      // così le due animazioni partono contemporaneamente
      this.fileRemovedFromDossier.emit(file);

      // Rimuovi dal servizio
      this.#relatedFilesService.removeFilesFromDossier(
        this.dossierForUpdate().id,
        [file.sha256],
      );

      // Dopo l'animazione, rimuovi effettivamente il file dalla lista locale
      setTimeout(() => {
        const currentDossier = this.dossierForUpdate();
        this.dossierForUpdate.set({
          ...currentDossier,
          files: currentDossier.files?.filter((f) => f.sha256 !== file.sha256),
        });
        this.animatingOutFiles.update((set) => {
          const newSet = new Set(set);
          newSet.delete(file.sha256);
          return newSet;
        });
      }, 400);

      notify(
        `File ${file.name} rimosso con successo dal dossier`,
        2000,
        'success',
      );
    } catch (error) {
      notify(`file ${file.name} non rimosso`, 2000, 'error');
    }
  }

  ngOnInit() {
    this.#breakpointObserver
      .observe([Breakpoints.Handset, Breakpoints.TabletPortrait])
      .subscribe((result) => {
        this.isMobile.set(result.matches);
      });
  }

  isAnimatingIn(sha256: string): boolean {
    return this.animatingInFiles().has(sha256);
  }

  isAnimatingOut(sha256: string): boolean {
    return this.animatingOutFiles().has(sha256);
  }

  triggerEntryAnimation(sha256: string) {
    this.animatingInFiles.update((set) => new Set(set).add(sha256));
    setTimeout(() => {
      this.animatingInFiles.update((set) => {
        const newSet = new Set(set);
        newSet.delete(sha256);
        return newSet;
      });
    }, 400);
  }

  selectDossierFile(file: SiaGridFile) {
    this.fileSelected.emit(file);
  }
}
